import { useEffect, useRef, useState } from "react";
import { BillingSettings } from "./invoices-section";
import { Product, ProductGroupItem } from "types/Product";

/** Everything the order page asks for, as the user left it. */
export type OrderDraftFields = {
  date: string;
  customer: string[];
  responsibleUser: string;
  description: string;
  /** Unfinished lines included — the page's saved list only has complete ones. */
  productLines: Product[];
  productGroups: ProductGroupItem[];
  billing: BillingSettings;
  isExternal: boolean;
  externalNumber: string;
};

/**
 * Raise it whenever OrderDraftFields changes shape: a draft stored by another
 * version of the page is dropped instead of restored into a form it no longer
 * fits.
 */
export const ORDER_DRAFT_VERSION = 1;

export type StoredOrderDraft = {
  version: number;
  savedAt: string;
  fields: OrderDraftFields;
};

// Typed as a Record so that a field added to OrderDraftFields does not compile
// until it is named here too.
const DRAFT_FIELDS: Record<keyof OrderDraftFields, true> = {
  date: true,
  customer: true,
  responsibleUser: true,
  description: true,
  productLines: true,
  productGroups: true,
  billing: true,
  isExternal: true,
  externalNumber: true,
};

const SAVE_DELAY_MS = 400;

/** One draft per company, customer-scoped page and order (or "new"). */
export function orderDraftKey(parts: {
  companyId: string;
  customerId?: string;
  orderId?: string;
}): string {
  return `orderDraft:${parts.companyId}:${parts.customerId || "--"}:${parts.orderId || "new"}`;
}

function removeOrderDraft(storageKey: string) {
  try {
    window.localStorage.removeItem(storageKey);
  } catch (error) {
    /* nothing to remove */
  }
}

function isCurrentDraft(draft: any): draft is StoredOrderDraft {
  return (
    draft?.version === ORDER_DRAFT_VERSION &&
    Object.keys(DRAFT_FIELDS).every((name) => draft.fields?.[name] != null)
  );
}

// Storage can be missing or full (private windows, blocked site data): a draft
// is a convenience, so every failure just means "no draft".
function readOrderDraft(storageKey: string): StoredOrderDraft | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (isCurrentDraft(draft)) return draft;
  } catch (error) {
    return null;
  }
  // Left by another version of the page: restoring it could break the page on
  // every visit, so it goes.
  removeOrderDraft(storageKey);
  return null;
}

function writeOrderDraft(storageKey: string, fields: OrderDraftFields) {
  try {
    const draft: StoredOrderDraft = {
      version: ORDER_DRAFT_VERSION,
      savedAt: new Date().toISOString(),
      fields,
    };
    window.localStorage.setItem(storageKey, JSON.stringify(draft));
  } catch (error) {
    /* the page keeps working without a draft */
  }
}

/**
 * Keeps the page's content in localStorage while it is being typed, so closing
 * the tab or going Back does not lose a half-made contract.
 *
 * - The content on the first render is the starting point: only content that
 *   differs from it is worth keeping, so opening a page and leaving writes
 *   nothing.
 * - A draft already stored when the page opens is offered as `foundDraft`;
 *   nothing is written until the user picks `restore` or `discard`, otherwise
 *   the first keystroke would overwrite the draft they are being asked about.
 * - `clear` is for after a successful save and also stops any write still
 *   waiting for its delay.
 */
export function useOrderDraft(storageKey: string, fields: OrderDraftFields) {
  const [foundDraft, setFoundDraft] = useState(() => readOrderDraft(storageKey));
  const [startingContent] = useState(() => JSON.stringify(fields));
  const isFinished = useRef(false);

  useEffect(() => {
    if (foundDraft || JSON.stringify(fields) === startingContent) return undefined;
    const timer = setTimeout(() => {
      if (!isFinished.current) writeOrderDraft(storageKey, fields);
    }, SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [foundDraft, fields, startingContent, storageKey]);

  const restore = (): OrderDraftFields | null => {
    const fieldsToApply = foundDraft ? foundDraft.fields : null;
    setFoundDraft(null);
    return fieldsToApply;
  };

  const discard = () => {
    removeOrderDraft(storageKey);
    setFoundDraft(null);
  };

  const clear = () => {
    isFinished.current = true;
    removeOrderDraft(storageKey);
  };

  return { foundDraft, restore, discard, clear };
}
